/*
 * zeroLabs: Newsletter-Anmeldung mit Gooey-Feld (snippets/zl-newsletter.liquid).
 * Gooey-Animation, eigene Arbeit von Moritz, für zeroLabs übernommen (Commit a71210a, 02.10.2026).
 *
 * Weg B (Moritz, 04.10.2026): Das Formular meldet direkt bei Klaviyo an, ohne Neuladen.
 * Öffentlicher Schlüssel und Listen-ID stehen als data-Attribute am Formular. Double Opt-in macht die Klaviyo-Liste.
 * Ein privater API-Schlüssel steht nie im Theme.
 *
 * Ohne Listen-ID (solange die CRM-Agentur sie nicht genannt hat) läuft ein Probelauf:
 * im Vorschau-Theme wird nichts gesendet und der Erfolg nur gezeigt, im Live-Theme erscheint die Fehlermeldung.
 *
 * Leere oder ungültige Adresse wird abgefangen, bevor das Gooey-Skript (assets/dm-gooey.js) etwas sieht:
 * Feld und Kugel schütteln sofort den Kopf, die Meldung steht ruhig darunter.
 * Alle Texte stehen im Snippet (data-Attribute), damit sie an einer Stelle liegen.
 */
(() => {
  const KLAVIYO_REVISION = '2026-07-15';
  const PROBE_MS = 1200; // Probelauf: so lange pulsiert die Kugel, bevor der Erfolg erscheint
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const token = (name, fallback) => {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return Number.isNaN(v) ? fallback : v;
  };

  const shake = (form) => {
    const body = form.querySelector('.newsletter-form-body');
    if (!body) return;
    body.classList.remove('nl-shake');
    void body.offsetWidth; // Animation neu starten, auch beim zweiten Fehler
    body.classList.add('nl-shake');
    // Nur auf das eigene Ende hören: die Gooey-Animationen melden ihr Ende ebenfalls bis hierher.
    const done = (e) => {
      if (e.target !== body || e.animationName !== 'nl-head-shake') return;
      body.classList.remove('nl-shake');
      body.removeEventListener('animationend', done);
    };
    body.addEventListener('animationend', done);
  };

  const clearError = (form) => form.querySelectorAll('.nl-error').forEach((el) => el.remove());

  // Meldung nach der Zeile, nicht darin: die Gooey-Zeile behält ihre Höhe, nur Feld und Kugel schütteln.
  const showError = (form, text) => {
    const body = form.querySelector('.newsletter-form-body');
    if (!body) return;
    clearError(form);
    const el = document.createElement('p');
    el.className = 'nl-error';
    el.setAttribute('role', 'alert');
    el.textContent = text;
    body.after(el);
    shake(form);
  };

  // dm-gooey.js liest "submitting" an Formular oder Knopf (oder disabled) und lässt die Kugel pulsieren.
  const setBusy = (form, busy) => {
    const btn = form.querySelector('.newsletter-form-button');
    form.classList.toggle('submitting', busy);
    if (btn) {
      btn.classList.toggle('submitting', busy);
      btn.disabled = busy;
    }
  };

  const showSuccess = (form) => {
    form.querySelector('.newsletter-form-body')?.setAttribute('hidden', '');
    form.querySelector('.newsletter-form-footnote')?.setAttribute('hidden', '');
    const box = form.querySelector('.newsletter-form-success');
    if (box) box.hidden = false;
  };

  const subscribe = async (form, email) => {
    const key = form.dataset.klaviyoKey;
    const list = form.dataset.klaviyoListe;
    if (!list) {
      const live = window.Shopify && Shopify.theme && Shopify.theme.role === 'main';
      if (live) throw new Error('Klaviyo-Liste fehlt');
      console.info('[zl-newsletter] Probelauf ohne Klaviyo-Liste, nichts gesendet.');
      await new Promise((r) => setTimeout(r, PROBE_MS));
      if (form.dataset.probe === 'fehler') throw new Error('Probelauf: Fehler nachgestellt');
      return;
    }
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: form.dataset.quelle || location.hostname + location.pathname,
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      }),
    });
    if (res.status !== 202 && !res.ok) throw new Error(`Klaviyo ${res.status}`);
  };

  // Mitwachsendes Feld ab 769 px: Ruhebreite, wächst mit langen Adressen bis zur Höchstbreite.
  // Die Kugel rückt mit und hält ihren Abstand. Am Handy füllt das Feld die Zeile.
  let measureCtx = null;
  const fitField = (form, input) => {
    const body = form.querySelector('.newsletter-form-body');
    if (!body) return;
    measureCtx = measureCtx || document.createElement('canvas').getContext('2d');
    if (!measureCtx) return;
    const cs = getComputedStyle(input);
    measureCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const spacing = parseFloat(cs.letterSpacing) || 0;
    const text = input.value;
    const textW = measureCtx.measureText(text).width + spacing * text.length;
    const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const rest = token('--zl-goo-field-rest', 360);
    const room = body.clientWidth - token('--zl-gap-element', 12) - token('--zl-control', 52);
    const max = Math.max(rest, Math.min(token('--zl-goo-field-max', 404), room));
    const w = Math.min(max, Math.max(rest, Math.ceil(textW + pad + token('--zl-goo-caret', 2))));
    form.style.setProperty('--zl-goo-field-w', `${w}px`); // token-ok: gemessene Breite, Grenzen aus zl-tokens.css
  };

  document.querySelectorAll('form.zl-newsletter').forEach((form) => {
    const input = form.querySelector('input[type="email"]');
    const btn = form.querySelector('.newsletter-form-button');
    if (!input || form.dataset.zlBereit) return; // Snippet zweimal auf der Seite (Labor und Fuß): nur einmal verbinden
    form.dataset.zlBereit = 'ja';
    form.noValidate = true; // eigene Prüfung statt Sprechblase des Browsers

    input.addEventListener('input', () => {
      clearError(form);
      fitField(form, input);
    });
    input.addEventListener('change', () => fitField(form, input));
    document.fonts?.ready.then(() => fitField(form, input));
    window.addEventListener('resize', () => fitField(form, input));

    const invalid = () => {
      const email = input.value.trim();
      if (!email) return form.dataset.msgLeer;
      if (!EMAIL_RE.test(email)) return form.dataset.msgUngueltig;
      return null;
    };

    // Ungültig: sofort Kopfschütteln, ohne Drücken und Impuls davor.
    // Capture-Phase am Formular, also bevor dm-gooey.js Feld oder Knopf sieht.
    const stopIfInvalid = (e) => {
      const msg = invalid();
      if (!msg) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type !== 'pointerdown') showError(form, msg);
    };
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.isComposing && e.target === input) stopIfInvalid(e);
    }, true);
    form.addEventListener('pointerdown', (e) => {
      if (btn && btn.contains(e.target)) stopIfInvalid(e);
    }, true);
    form.addEventListener('click', (e) => {
      if (btn && btn.contains(e.target)) stopIfInvalid(e);
    }, true);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const msg = invalid();
      if (msg) return showError(form, msg);
      if (form.classList.contains('submitting')) return;
      if (form.hasAttribute('data-zl-fertig')) return;
      clearError(form);
      setBusy(form, true);
      try {
        await subscribe(form, input.value.trim());
      } catch (err) {
        console.warn('[zl-newsletter]', err);
        setBusy(form, false);
        showError(form, form.dataset.msgFehler);
        return;
      }
      // Angekommen: Pfeil wird zum Haken, die Kugel geht zurück ins Feld (dm-gooey.js), dann steht die Bestätigung.
      form.setAttribute('data-zl-fertig', '');
      setBusy(form, false);
      const body = form.querySelector('.newsletter-form-body');
      if (body && window.zlGoo && window.zlGoo.fertig) await window.zlGoo.fertig(body);
      showSuccess(form);
    });
  });
})();
